import React, { useState } from "react";
import "../../components/login/login.css";
import {
  Alert,
  Box,
  Card,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "../../constants/constants";
import { LoadingButton } from "@mui/lab";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import RdMyModal from "../../widgets/myModal/RdMyModal";
import PasswordReset from "./PasswordReset";
import axios from "axios";
import {
  getDbHeader,
  getErrorResponse,
  toastError,
} from "../../utils2024/utils";
import { useNavigate } from "react-router-dom";
import { saveUser } from "../../utility/utility";
import useGetEventSettings from "../hooks/useGetEventSettings";
import MuPb from "../../widgets/MuPb";

const VisitorLogin = () => {
  const { data, isLoading, isError, error: loadError } = useGetEventSettings();
  const navigate = useNavigate();
  const [error, setError] = useState();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showLoginInfo, setShowLoginInfo] = useState(false);
  const [emailEntered, setEmailEntered] = useState(false);
  const [rememberChecked, setRememberChecked] = useState(false);

  const onLogin = async () => {
    if (username === "") {
      toast.error("Please enter Email Id");
    } else if (password === "") {
      toast.error("Enter password");
    } else {
      try {
        setIsLoggingIn(true);
        const { data } = await axios.post(
          `${API_ENDPOINT}user/login`,
          { username, password },
          getDbHeader()
        );
        saveUser(data.data);
        navigate(`/${data.data.role}`);
      } catch (error) {
        setError(error?.response?.data);
      } finally {
        setIsLoggingIn(false);
      }
    }
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const rememberPwd = () => {
    setRememberChecked((prev) => !prev);
  };

  const handleForgotPwd = () => {
    setShowPasswordReset(true);
  };

  const handleCloseDialog = () => {
    setShowLoginInfo(false);
  };

  const onSubmit = async () => {
    if (emailEntered) {
      onLogin();
    } else {
      try {
        setIsLoggingIn(true);
        const response = await axios.post(
          `${API_ENDPOINT}user/check-email-account`,
          { email: username },
          getDbHeader()
        );
        if (response.data.pwdExists) {
          setEmailEntered(true);
        } else {
          setShowLoginInfo(true);
        }
      } catch (error) {
        toastError(error);
      } finally {
        setIsLoggingIn(false);
      }
    }
  };

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(loadError)}</Alert>;
  }

  return (
    <div className="expp-login-ca">
      <RdMyModal
        mWidth={"500px"}
        showModal={showPasswordReset}
        setShowModal={setShowPasswordReset}
        modalC={
          <PasswordReset
            show={showPasswordReset}
            setShow={setShowPasswordReset}
            banner={data?.eventBanner}
          />
        }
      />
      <Dialog
        open={showLoginInfo}
        onClose={handleCloseDialog}
        aria-labelledby="login-info-dialog-title"
        aria-describedby="login-info-dialog-description"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "8px",
            padding: "16px",
            maxWidth: "400px",
          },
        }}
      >
        <DialogTitle id="login-info-dialog-title" sx={{ fontWeight: 600 }}>
          Password Not Set
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="login-info-dialog-description"
            sx={{ color: "#333" }}
          >
            You haven't set a password for your account. A password reset link
            has been sent to <strong>{username}</strong>. Please check your
            inbox (and spam/junk folder) to set your password and continue.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            sx={{
              backgroundColor: "#054054",
              "&:hover": {
                backgroundColor: "#0B6B6B",
              },
              textTransform: "none",
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <div className="login-box">
        <Card
          sx={{
            borderRadius: 0,
            backgroundImage: `url(${data?.loginBanner})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
          className="login-flex-left"
        ></Card>
        <div className="login-flex-right">
          <div className="login-action-box">
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box>
                <Box
                  sx={{
                    textAlign: "center",
                    fontSize: "20px",
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Welcome to
                </Box>
                <img
                  src={
                    "https://s3.ap-south-1.amazonaws.com/expoplanner.in/download.png"
                  }
                  alt=""
                />
              </Box>
            </div>
            {error && <Alert severity="error">{error.message}</Alert>}
            <div style={{ marginTop: "28px" }}>
              <TextField
                value={username}
                disabled={emailEntered}
                onChange={(e) => setUsername(e.target.value)}
                variant="outlined"
                size="small"
                label="Email Id"
                error={
                  username
                    ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username)
                    : false
                }
                helperText={
                  username && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username)
                    ? "Please enter a valid email"
                    : ""
                }
                fullWidth
              />
              {emailEntered && (
                <FormControl
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                  sx={{ marginTop: 3 }}
                >
                  <InputLabel htmlFor="standard-adornment-password">
                    Password
                  </InputLabel>
                  <OutlinedInput
                    id="standard-adornment-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {!showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Password"
                  />
                </FormControl>
              )}

              <div style={{ marginTop: "28px" }}>
                {emailEntered && (
                  <div style={{ display: "flex" }}>
                    <FormControlLabel
                      sx={{
                        flex: 1,
                        color: "#777",
                        "& .MuiTypography-root": {
                          fontSize: "15px",
                        },
                        "& .MuiCheckbox-root": {
                          "& .MuiSvgIcon-root": {
                            fontSize: "18px",
                          },
                        },
                      }}
                      label="Remember Password"
                      control={
                        <Checkbox
                          checked={rememberChecked}
                          onChange={rememberPwd}
                        />
                      }
                    />
                    <div
                      style={{
                        textDecoration: "underline",
                        fontSize: "15px",
                        alignSelf: "center",
                        display: "flex",
                        justifyContent: "end",
                        cursor: "pointer",
                        color: "#888",
                      }}
                      onClick={handleForgotPwd}
                    >
                      Forgot your password?
                    </div>
                  </div>
                )}

                <div style={{ marginTop: "16px" }}>
                  <LoadingButton
                    onClick={onSubmit}
                    loading={isLoggingIn}
                    fullWidth
                    sx={{
                      backgroundColor: "#054054",
                      "&:hover": {
                        backgroundColor: "#0B6B6B",
                      },
                    }}
                    variant="contained"
                  >
                    {emailEntered ? `Sign in` : `Proceed`}
                  </LoadingButton>
                </div>

                <div style={{ marginTop: "16px", textAlign: "center" }}>
                  <span style={{ fontSize: "15px", color: "#888" }}>
                    Haven't registered yet?{" "}
                  </span>
                  <a
                    href="https://pmtx2026-registration.expoplanner.in/visitor-registration"
                    style={{
                      color: "#054054",
                      fontWeight: "bold",
                      textDecoration: "none",
                    }}
                  >
                    Register now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorLogin;
